import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { BookOpen, ArrowRight } from "lucide-react";
import { EditorialCard } from "../components/editorial-card";
import type { HomepageArticle } from "@/components/homepage/types";

interface LatestArticlesSectionProps {
  items: HomepageArticle[];
  isLoading: boolean;
}

export function LatestArticlesSection({ items, isLoading }: LatestArticlesSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 8) : [];

  if (!displayItems.length && !isLoading) {
    return (
      <section className="border-b border-border bg-neutral-50/50" aria-labelledby="latest-articles-heading">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
          <SectionHeader eyebrow="Latest" icon={BookOpen} title="Latest Articles" href="/articles" />
          <EmptyState icon={BookOpen} title="No articles yet" description="Check back soon for new content." />
        </div>
      </section>
    );
  }

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="latest-articles-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader eyebrow="Latest" icon={BookOpen} title="Latest Articles" href="/articles" />
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3">
                <SectionLoader variant="card" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item) => (
              <EditorialCard
                key={item.id}
                title={item.title}
                author={item.author}
                readTime={item.readTime}
                category={item.category ?? "Article"}
                date={item.createdAt}
                href={`/news/${item.id}`}
                variant="default"
                tags={item.tags}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}